import { DateTime, Info } from "luxon";
import { Device } from "../../../../types/types";
import { useFetch } from "../../../../hooks/useFetch";
import { useEffect, useState } from "react";
import ReactEcharts, { EChartsOption } from "echarts-for-react";
import HeatMap from "../../../../components/Charts/Heatmap";
import { Tooltip } from "@radix-ui/themes";
import { t } from "i18next";
import { TbChevronLeft, TbChevronRight } from "react-icons/tb";

type Props = {
  currentDevice: Device | undefined;
  selectedDate: {
    from: Date,
    to: Date,
  },
  setSelectedDate: Function;
};

const today = DateTime.local();

const DeviceIntensivity = ({ currentDevice, selectedDate, setSelectedDate }: Props) => {
  const [intensivity, setIntensivity] = useState<any>([]);
  const [intensivityLoaded, setIntensivityLoaded] = useState(false);
  const [intensivityStartDate, setIntensivityStartDate] = useState(DateTime.local().endOf("month").minus({ year: 1 }));
  const [intensivityEndDate, setIntensivityEndDate] = useState(DateTime.local().endOf("month"));

  const [
    { response: intensivityResponse, isLoading: intensivityLoading, error: intensivityError },
    doFetchIntensivity,
  ] = useFetch(`/devices/devices/${currentDevice?.id}/daily_usage?from=${intensivityStartDate}&to=${intensivityEndDate}`);

  useEffect(() => {
    doFetchIntensivity()
  }, [currentDevice?.id])

  useEffect(() => {
    if (!intensivityResponse) {
      return;
    }
    // @ts-ignore
    intensivityResponse?.data?.map((val) => {
      delete Object.assign(val, { ["date"]: val["at"] })["at"];
      delete Object.assign(val, { ["count"]: val["online"] })["online"];
    });
    setIntensivity(intensivityResponse?.data);
    setIntensivityLoaded(true);
  }, [intensivityResponse]);

  const increaseYear = () => {
    setIntensivityStartDate(intensivityStartDate.minus({ year: 1 }));
    setIntensivityEndDate(intensivityEndDate.minus({ year: 1 }));
    doFetchIntensivity();
  };

  const decreaseYear = () => {
    setIntensivityStartDate(intensivityStartDate.plus({ year: 1 }));
    setIntensivityEndDate(intensivityEndDate.plus({ year: 1 }));
    doFetchIntensivity();
  };

  function convertHeatMapDate(date: string) {
    const parts = date.split("/").map(Number);
    return DateTime.local(parts[0], parts[1], parts[2]);
  }

  function handleRectClick(date: string) {
    let d = new Date(date)
    let sd = DateTime.fromJSDate(d).startOf("day").toJSDate();
    let ed = DateTime.fromJSDate(d).endOf("day").toJSDate();
    setSelectedDate({ from: sd, to: ed });
  }

  function setColors() {
    let d = 86400;
    let obj = {};
    Object.assign(obj, { [d / d]: "rgba(255, 255, 255, 0.1)" });
    Object.assign(obj, { [d * 0.1]: "#FFF48E" });
    Object.assign(obj, { [d * 0.25]: "#CAE781" });
    Object.assign(obj, { [d * 0.4]: "#94D973" });
    Object.assign(obj, { [d * 0.5]: "#28BD57" });
    Object.assign(obj, { [d * 0.65]: "#1B9D9B" });
    Object.assign(obj, { [d * 0.8]: "#148DBD" });
    Object.assign(obj, { [d * 0.9]: "#0D7DDE" });
    Object.assign(obj, { [d * 1]: "#3B82F6" });
    return obj;
  }

  function legendText() {
    return (
      <div className="flex flex-row h-4 w-full pt-2 pl-2 flex-shrink-0 items-center justify-center">
        <div className="flex items-center mr-3" style={{ fontSize: 9 }}>
          <span className="block w-2 h-2 mr-2" style={{ backgroundColor: "#f3f4f6" }}></span>0%
        </div>
        <div className="flex items-center mr-3" style={{ fontSize: 9 }}>
          <span className="block w-2 h-2 mr-2" style={{ backgroundColor: "#FFF48E" }}></span>10%
        </div>
        <div className="flex items-center mr-3" style={{ fontSize: 9 }}>
          <span className="block w-2 h-2 mr-2" style={{ backgroundColor: "#CAE781" }}></span>25%
        </div>
        <div className="flex items-center mr-3" style={{ fontSize: 9 }}>
          <span className="block w-2 h-2 mr-2" style={{ backgroundColor: "#94D973" }}></span>40%
        </div>
        <div className="flex items-center mr-3" style={{ fontSize: 9 }}>
          <span className="block w-2 h-2 mr-2" style={{ backgroundColor: "#28BD57" }}></span>50%
        </div>
        <div className="flex items-center mr-3" style={{ fontSize: 9 }}>
          <span className="block w-2 h-2 mr-2" style={{ backgroundColor: "#1B9D9B" }}></span>65%
        </div>
        <div className="flex items-center mr-3" style={{ fontSize: 9 }}>
          <span className="block w-2 h-2 mr-2" style={{ backgroundColor: "#148DBD" }}></span>80%
        </div>
        <div className="flex items-center mr-3" style={{ fontSize: 9 }}>
          <span className="block w-2 h-2 mr-2" style={{ backgroundColor: "#0D7DDE" }}></span>90%
        </div>
        <div className="flex items-center mr-3" style={{ fontSize: 9 }}>
          <span className="block w-2 h-2 mr-2" style={{ backgroundColor: "#3B82F6" }}></span>100%
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col py-6 flex-grow max-w-[1280px] mx-auto">
      {!intensivityLoaded ? (
        <div className="flex items-center justify-center w-full h-60">
          <div className="loader ease-linear rounded-full border-2 border-t-2 h-4 w-4"></div>
        </div>
      ) : (
      <div className="flex flex-col h-60">
        <div className="flex justify-between text-sm mb-4 items-center">
          {intensivityEndDate.year === today.year ? (
            <>
              <button
                className="bg-blueA3 rounded-md px-1 py-1 flex items-center hover:bg-muted w-16 space-x-2"
                onClick={() => increaseYear()}
              >
                <TbChevronLeft className="w-3.5 h-3.5 flex-none" />
                <span>{intensivityEndDate.year - 1}</span>
              </button>
              <span className="font-medium">{intensivityEndDate.year}</span>
              <div className="w-16"></div>
            </>
          ) : (
            <>
              <button
                className="bg-blueA3 rounded px-1 py-1 flex items-center hover:bg-muted w-16 space-x-2"
                onClick={() => increaseYear()}
              >
                <TbChevronLeft className="w-3.5 h-3.5 flex-none" />
                <span>{intensivityEndDate.year}</span>
              </button>
              <span className="font-medium">{intensivityEndDate.year}</span>
              <button
                className="bg-blueA3 rounded px-1 py-1 flex items-center hover:bg-muted w-16 space-x-2"
                onClick={() => decreaseYear()}
              >
                <span>{intensivityEndDate.year + 1}</span>
                <TbChevronRight className="w-3.5 h-3.5 flex-none" />
              </button>
            </>
          )}
        </div>
        <div className="flex flex-row items-start">
          <div className="flex flex-col h-full w-8 pt-5 pl-2 flex-shrink-0">
            {[Info.weekdays("short")[6], Info.weekdays("short").splice(0, 6)].flat().map((el, idx) => {
              return (
                <span
                  key={idx}
                  className="h-4 flex items-center capitalize"
                  style={{ fontSize: 9, marginBottom: 7 }}
                >
                  {el}
                </span>
              );
            })}
          </div>
          <div className="flex flex-col items-end justify-end overflow-hidden">
            <HeatMap
              startDate={intensivityStartDate.toJSDate()}
              endDate={intensivityEndDate.toJSDate()}
              className={"h-44 w-full overflow-hidden"}
              style={{ width: (365 / 7) * 24 - 24, fontSize: 9 }}
              value={intensivity}
              rectSize={18}
              radius={4}
              space={5}
              legendCellSize={0}
              rectRender={(props, data) => {
                if (selectedDate?.from && selectedDate?.to) {
                  if (
                    convertHeatMapDate(data.date).startOf("day") >=
                      DateTime.fromJSDate(selectedDate?.from).startOf("day") &&
                    convertHeatMapDate(data.date).endOf("day") <= DateTime.fromJSDate(selectedDate?.to)
                  ) {
                    // @ts-ignore
                    props.fill = "#3728BD";
                  }
                }
                return (
                  <Tooltip key={props.key} content={`${t("date")}: ${data.date}`}>
                    <rect {...props} onClick={() => handleRectClick(data.date)}></rect>
                  </Tooltip>
                );
              }}
              panelColors={setColors()}
            />
          </div>
        </div>
        {legendText()}
      </div>
      )}
    </div>
  );
};

export default DeviceIntensivity;
